import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { useCart } from "../context/CartContext";
import Navbar from "../components/Navbar";

const CheckoutPage = () => {
  const { cartItems, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    card: "",
    expiry: "",
    cvv: "",
    cardName: "",
  });

  const titleRef = useRef(null);
  const stepsRef = useRef(null);
  const formRef = useRef(null);
  const summaryRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Title drops in
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: -30 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out", delay: 0.1 },
      );
      // Steps slide in from left
      gsap.fromTo(
        stepsRef.current?.children || [],
        { opacity: 0, x: -30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.5,
          ease: "power2.out",
          stagger: 0.1,
          delay: 0.25,
        },
      );
      // Form slides up
      gsap.fromTo(
        formRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power2.out", delay: 0.35 },
      );
      // Summary slides up with slight delay
      gsap.fromTo(
        summaryRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power2.out", delay: 0.5 },
      );
    });
    return () => ctx.revert();
  }, []);

  // Re-animate form when step changes
  useEffect(() => {
    if (formRef.current) {
      gsap.fromTo(
        formRef.current,
        { opacity: 0, x: 30 },
        { opacity: 1, x: 0, duration: 0.45, ease: "power2.out" },
      );
    }
  }, [step]);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleOrder = () => {
    setStep(3);
    clearCart();
  };

  if (step === 3) {
    return (
      <Page>
        <Navbar />
        <SuccessWrapper>
          <SuccessGlow />
          <SuccessIcon>✓</SuccessIcon>
          <SuccessTitle>Porosia u Konfirmua!</SuccessTitle>
          <SuccessSub>
            Faleminderit për besimin tuaj. Do t'ju kontaktojmë së shpejti.
          </SuccessSub>
          <HomeBtn onClick={() => navigate("/")}>← Kthehu në Faqe</HomeBtn>
        </SuccessWrapper>
      </Page>
    );
  }

  return (
    <Page>
      <Navbar />
      <Inner>
        <PageTitle ref={titleRef}>Checkout</PageTitle>

        <Steps ref={stepsRef}>
          {["Informacioni", "Pagesa", "Konfirmimi"].map((s, i) => (
            <Step key={s} $active={step === i + 1} $done={step > i + 1}>
              <StepNum $active={step === i + 1} $done={step > i + 1}>
                {step > i + 1 ? "✓" : i + 1}
              </StepNum>
              <StepLabel $active={step === i + 1}>{s}</StepLabel>
            </Step>
          ))}
        </Steps>

        <Grid>
          {/* Left: form */}
          <FormSide ref={formRef}>
            {step === 1 && (
              <FormSection>
                <FormTitle>Informacioni i Dërgimit</FormTitle>
                <FormRow>
                  <Field>
                    <Label>Emri i plotë</Label>
                    <Input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Ardit Krasniqi"
                    />
                  </Field>
                  <Field>
                    <Label>Email</Label>
                    <Input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="ardit@email.com"
                    />
                  </Field>
                </FormRow>
                <Field>
                  <Label>Numri i telefonit</Label>
                  <Input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+383 44 xxx xxx"
                  />
                </Field>
                <Field>
                  <Label>Adresa</Label>
                  <Input
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Rruga Fehmi Agani, Nr. 12"
                  />
                </Field>
                <Field>
                  <Label>Qyteti</Label>
                  <Input
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="Prishtinë"
                  />
                </Field>
                <NextBtn onClick={() => setStep(2)}>
                  Vazhdo me Pagesen →
                </NextBtn>
              </FormSection>
            )}

            {step === 2 && (
              <FormSection>
                <FormTitle>Informacioni i Pagesës</FormTitle>
                <Field>
                  <Label>Emri në kartë</Label>
                  <Input
                    name="cardName"
                    value={form.cardName}
                    onChange={handleChange}
                    placeholder="ARDIT KRASNIQI"
                  />
                </Field>
                <Field>
                  <Label>Numri i kartës</Label>
                  <Input
                    name="card"
                    value={form.card}
                    onChange={handleChange}
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                  />
                </Field>
                <FormRow>
                  <Field>
                    <Label>Data e skadencës</Label>
                    <Input
                      name="expiry"
                      value={form.expiry}
                      onChange={handleChange}
                      placeholder="MM/YY"
                      maxLength={5}
                    />
                  </Field>
                  <Field>
                    <Label>CVV</Label>
                    <Input
                      name="cvv"
                      value={form.cvv}
                      onChange={handleChange}
                      placeholder="123"
                      maxLength={3}
                      type="password"
                    />
                  </Field>
                </FormRow>
                <CardBrands>
                  <CardIcon>💳</CardIcon>
                  <CardNote>Të gjitha kartat kryesore pranohen</CardNote>
                </CardBrands>
                <BtnRow>
                  <BackStepBtn onClick={() => setStep(1)}>← Kthehu</BackStepBtn>
                  <NextBtn onClick={handleOrder}>Konfirmo Porosinë ✓</NextBtn>
                </BtnRow>
              </FormSection>
            )}
          </FormSide>

          {/* Right: order summary */}
          <SummarySide ref={summaryRef}>
            <SummaryTitle>Përmbledhja e Porosisë</SummaryTitle>

            <ItemsList>
              {cartItems.length === 0 ? (
                <EmptyNote>Shporta është bosh.</EmptyNote>
              ) : (
                cartItems.map((item) => (
                  <SummaryItem key={item.id}>
                    <SummaryImgWrap>
                      <SummaryImg src={item.image} alt={item.name} />
                    </SummaryImgWrap>
                    <SummaryInfo>
                      <SummaryName>{item.name}</SummaryName>
                      <SummaryBrand>
                        {item.brand} · x{item.qty}
                      </SummaryBrand>
                      <SummaryPrice>
                        €{(item.price * item.qty).toFixed(2)}
                      </SummaryPrice>
                    </SummaryInfo>
                  </SummaryItem>
                ))
              )}
            </ItemsList>

            <SummaryDivider />

            <SummaryRow>
              <SummaryLabel>Nëntotali</SummaryLabel>
              <SummaryVal>€{total.toFixed(2)}</SummaryVal>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Dërgesa</SummaryLabel>
              <SummaryVal style={{ color: "#5cba7d" }}>Falas</SummaryVal>
            </SummaryRow>

            <SummaryDivider />

            <SummaryRow>
              <SummaryLabel>Totali</SummaryLabel>
              <TotalAmount>€{total.toFixed(2)}</TotalAmount>
            </SummaryRow>

            <SecureNote>
              🔒 Pagesa juaj është e sigurt dhe e enkriptuar
            </SecureNote>
          </SummarySide>
        </Grid>
      </Inner>
    </Page>
  );
};

export default CheckoutPage;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const scaleIn = keyframes`
  from { transform: scale(0); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.3; }
  50%       { opacity: 0.7; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  background: var(--bg);
  min-height: 100vh;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 7rem 4rem 6rem;
`;

const PageTitle = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(3rem, 6vw, 5rem);
  color: var(--white);
  margin-bottom: 3rem;
  letter-spacing: -0.01em;
  opacity: 0;
`;

const Steps = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 4rem;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding-right: 2.5rem;
  position: relative;
  opacity: 0;

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: 0.8rem;
    top: 50%;
    width: 1.5rem;
    height: 1px;
    background: ${({ $done }) =>
      $done ? "var(--accent)" : "rgba(139,58,26,0.25)"};
    transition: background 0.4s ease;
  }
`;

const StepNum = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1.5px solid
    ${({ $active, $done }) =>
      $active || $done ? "var(--accent)" : "rgba(139,58,26,0.25)"};
  background: ${({ $done }) => ($done ? "var(--accent)" : "transparent")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-condensed);
  font-size: 0.75rem;
  font-weight: 700;
  color: ${({ $active, $done }) =>
    $active
      ? "var(--accent)"
      : $done
        ? "var(--white)"
        : "rgba(240,236,230,0.3)"};
  transition: all 0.4s ease;
`;

const StepLabel = styled.span`
  font-family: var(--font-condensed);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ $active }) =>
    $active ? "var(--white)" : "rgba(240,236,230,0.3)"};
  transition: color 0.4s ease;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 4rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormSide = styled.div`
  opacity: 0;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const FormTitle = styled.h3`
  font-family: var(--font-display);
  font-size: 1.8rem;
  color: var(--white);
  margin-bottom: 0.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-family: var(--font-condensed);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(240, 236, 230, 0.4);
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(139, 58, 26, 0.2);
  border-radius: 4px;
  padding: 0.9rem 1rem;
  font-family: var(--font-body);
  font-size: 0.92rem;
  color: var(--white);
  outline: none;
  transition:
    border-color 0.25s ease,
    background 0.25s ease;

  &::placeholder {
    color: rgba(240, 236, 230, 0.2);
  }
  &:focus {
    border-color: var(--accent);
    background: rgba(139, 58, 26, 0.05);
  }
`;

const CardBrands = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(139, 58, 26, 0.1);
  border-radius: 4px;
`;

const CardIcon = styled.span`
  font-size: 1.5rem;
`;

const CardNote = styled.span`
  font-family: var(--font-body);
  font-size: 0.8rem;
  color: var(--white-dim);
`;

const BtnRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const NextBtn = styled.button`
  flex: 1;
  padding: 1rem 2rem;
  background: var(--accent);
  border: none;
  border-radius: 999px;
  font-family: var(--font-condensed);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--white);
  cursor: pointer;
  margin-top: 0.5rem;
  transition: all 0.25s ease;
  &:hover {
    background: var(--accent-light);
    transform: scale(1.02);
  }
`;

const BackStepBtn = styled.button`
  padding: 1rem 1.6rem;
  background: transparent;
  border: 1px solid rgba(139, 58, 26, 0.25);
  border-radius: 999px;
  font-family: var(--font-condensed);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--white-dim);
  cursor: pointer;
  margin-top: 0.5rem;
  transition: all 0.22s ease;
  &:hover {
    border-color: var(--accent);
    color: var(--white);
  }
`;

/* Summary */
const SummarySide = styled.div`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(139, 58, 26, 0.15);
  border-radius: 6px;
  padding: 2rem;
  position: sticky;
  top: 6rem;
  opacity: 0;
`;

const SummaryTitle = styled.h3`
  font-family: var(--font-display);
  font-size: 1.4rem;
  color: var(--white);
  margin-bottom: 1.5rem;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 340px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--accent);
    border-radius: 2px;
  }
`;

const EmptyNote = styled.p`
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--white-dim);
  text-align: center;
  padding: 2rem 0;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.8rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(139, 58, 26, 0.08);
  border-radius: 4px;
  transition: border-color 0.2s;
  &:hover {
    border-color: rgba(139, 58, 26, 0.25);
  }
`;

const SummaryImgWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
`;

const SummaryImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const SummaryInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SummaryName = styled.p`
  font-family: var(--font-condensed);
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--white);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SummaryBrand = styled.p`
  font-family: var(--font-body);
  font-size: 0.7rem;
  color: var(--white-dim);
  margin-top: 2px;
`;

const SummaryPrice = styled.p`
  font-family: var(--font-display);
  font-size: 1rem;
  color: var(--accent);
  margin-top: 4px;
`;

const SummaryDivider = styled.div`
  height: 1px;
  background: rgba(139, 58, 26, 0.15);
  margin: 1.2rem 0;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SummaryLabel = styled.span`
  font-family: var(--font-condensed);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(240, 236, 230, 0.4);
`;

const SummaryVal = styled.span`
  font-family: var(--font-body);
  font-size: 0.9rem;
  color: var(--white);
`;

const TotalAmount = styled.span`
  font-family: var(--font-display);
  font-size: 2rem;
  color: var(--white);
`;

const SecureNote = styled.p`
  font-family: var(--font-body);
  font-size: 0.72rem;
  color: rgba(240, 236, 230, 0.3);
  text-align: center;
  margin-top: 1.2rem;
`;

/* Success screen */
const SuccessWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  gap: 1.5rem;
  text-align: center;
  padding: 4rem;
  position: relative;
`;

const SuccessGlow = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(139, 58, 26, 0.3), transparent 70%);
  filter: blur(60px);
  animation: ${glowPulse} 3s ease-in-out infinite;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: var(--white);
  position: relative;
  z-index: 1;
  animation: ${scaleIn} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
`;

const SuccessTitle = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: var(--white);
  position: relative;
  z-index: 1;
`;

const SuccessSub = styled.p`
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: 300;
  color: var(--white-dim);
  max-width: 400px;
  line-height: 1.7;
  position: relative;
  z-index: 1;
`;

const HomeBtn = styled.button`
  padding: 1rem 2.5rem;
  background: var(--accent);
  border: none;
  border-radius: 999px;
  font-family: var(--font-condensed);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--white);
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: all 0.25s ease;
  &:hover {
    background: var(--accent-light);
    transform: scale(1.04);
  }
`;
